/**
 * Pure logic for the "Who starts?" finger picker (Chwazi-style).
 *
 * Everyone puts a finger on the screen; once the set of fingers has been
 * stable for COUNTDOWN_MS one finger is picked uniformly at random.
 * Adding or lifting a finger restarts the countdown.
 *
 * Teams mode uses the same countdown but splits all fingers at random into
 * evenly sized teams (bigger teams first, e.g. 7 into 2 is 4 + 3).
 */

import { teamSizes } from './fairTeams';

export const COUNTDOWN_MS = 3000;
export const MIN_FINGERS = 2;

/**
 * Finger colours, all readable on the dark (stone-900) background with
 * at least 7:1 contrast and spread around the hue wheel.
 * Fingers also carry a number so colour is never the only cue.
 */
export const FINGER_COLORS = [
  '#F87171', // red-400
  '#60A5FA', // blue-400
  '#34D399', // emerald-400
  '#FBBF24', // amber-400
  '#C084FC', // purple-400
  '#F472B6', // pink-400
  '#22D3EE', // cyan-400
  '#A3E635', // lime-400
  '#FB923C', // orange-400
  '#E7E5E4', // stone-200
];

/** Team colours and letters, in the same palette as the fingers. */
export const TEAMS = [
  { letter: 'A', name: 'Red', color: '#F87171' },
  { letter: 'B', name: 'Blue', color: '#60A5FA' },
  { letter: 'C', name: 'Green', color: '#34D399' },
  { letter: 'D', name: 'Yellow', color: '#FBBF24' },
  { letter: 'E', name: 'Purple', color: '#C084FC' },
  { letter: 'F', name: 'Pink', color: '#F472B6' },
];

export type PickerMode = 'pick' | 'teams';

/** Split into a fixed number of teams, or into teams of a fixed size. */
export type TeamSetup = { kind: 'count'; value: number } | { kind: 'size'; value: number };

export const TEAM_COUNT_OPTIONS = [2, 3, 4];
export const TEAM_SIZE_OPTIONS = [2, 3];

/** How many teams the fingers are split into (capped by the colour palette). */
export function teamCountFor(setup: TeamSetup, fingerCount: number): number {
  const count = setup.kind === 'count' ? setup.value : Math.ceil(fingerCount / setup.value);
  return Math.min(count, TEAMS.length);
}

/** Fewest fingers that make a split worth doing: every team gets at least one. */
export function minFingersFor(mode: PickerMode, setup: TeamSetup): number {
  if (mode === 'pick') return MIN_FINGERS;
  return setup.kind === 'count' ? Math.max(MIN_FINGERS, setup.value) : setup.value + 1;
}

/** Fisher-Yates shuffle driven by the unbiased CSPRNG. */
export function secureShuffle<T>(items: T[], random?: RandomSource): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = secureRandomInt(i + 1, random);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Random even split: finger id -> team index. Team sizes differ by at most
 * one and the bigger teams come first.
 */
export function splitIntoTeams(fingerIds: number[], teamCount: number, random?: RandomSource): Record<number, number> {
  if (teamCount < 1) throw new Error(`Need at least one team, got ${teamCount}`);
  const order = secureShuffle(fingerIds, random);
  const sizes = teamSizes(order.length, teamCount);
  const teamOf: Record<number, number> = {};
  let next = 0;
  sizes.forEach((size, team) => {
    for (let k = 0; k < size; k++) teamOf[order[next++]] = team;
  });
  return teamOf;
}

export interface Finger {
  id: number; // PointerEvent.pointerId
  x: number;
  y: number;
  color: string;
  label: number; // 1-based, lowest free number
}

export type PickerPhase = 'waiting' | 'counting' | 'picked';

export interface PickerState {
  mode: PickerMode;
  teamSetup: TeamSetup;
  phase: PickerPhase;
  fingers: Finger[];
  /** Epoch ms when the pick happens, null when not counting down. */
  deadline: number | null;
  winnerId: number | null;
  /** Teams mode result: finger id -> index into TEAMS. */
  teamOf: Record<number, number> | null;
}

export type PickerAction =
  | { type: 'down'; id: number; x: number; y: number; now: number }
  | { type: 'move'; id: number; x: number; y: number }
  | { type: 'up'; id: number; now: number }
  | { type: 'tick'; now: number; random?: RandomSource }
  | { type: 'reset' }
  | { type: 'mode'; mode: PickerMode; now: number }
  | { type: 'teamSetup'; setup: TeamSetup; now: number };

/** Fills the array with random 32-bit values (crypto.getRandomValues signature). */
export type RandomSource = (array: Uint32Array) => Uint32Array;

const defaultRandom: RandomSource = (array) => crypto.getRandomValues(array);

/**
 * Uniform random integer in [0, n) from a CSPRNG, using rejection sampling
 * so there is no modulo bias.
 */
export function secureRandomInt(n: number, random: RandomSource = defaultRandom): number {
  if (!Number.isInteger(n) || n <= 0) {
    throw new Error(`secureRandomInt needs a positive integer, got ${n}`);
  }
  const range = 0x100000000; // 2^32
  const limit = range - (range % n);
  const buf = new Uint32Array(1);
  // Expected iterations < 2; bounded to avoid an infinite loop on a broken source.
  for (let i = 0; i < 1000; i++) {
    random(buf);
    if (buf[0] < limit) return buf[0] % n;
  }
  throw new Error('Random source kept returning out-of-range values');
}

export function pickRandom<T>(items: T[], random?: RandomSource): T {
  if (items.length === 0) throw new Error('Cannot pick from an empty list');
  return items[secureRandomInt(items.length, random)];
}

/** First palette colour no current finger uses (cycles if all are taken). */
export function nextColor(fingers: Finger[]): string {
  const used = new Set(fingers.map((f) => f.color));
  return FINGER_COLORS.find((c) => !used.has(c)) ?? FINGER_COLORS[fingers.length % FINGER_COLORS.length];
}

/** Lowest positive label no current finger uses. */
export function nextLabel(fingers: Finger[]): number {
  const used = new Set(fingers.map((f) => f.label));
  let label = 1;
  while (used.has(label)) label++;
  return label;
}

function withCountdown(state: PickerState, fingers: Finger[], now: number): PickerState {
  const enough = fingers.length >= minFingersFor(state.mode, state.teamSetup);
  return {
    ...state,
    phase: enough ? 'counting' : 'waiting',
    fingers,
    deadline: enough ? now + COUNTDOWN_MS : null,
    winnerId: null,
    teamOf: null,
  };
}

export const initialPickerState: PickerState = {
  mode: 'pick',
  teamSetup: { kind: 'count', value: 2 },
  phase: 'waiting',
  fingers: [],
  deadline: null,
  winnerId: null,
  teamOf: null,
};

export function pickerReducer(state: PickerState, action: PickerAction): PickerState {
  switch (action.type) {
    case 'down': {
      // Once a winner is shown, new touches don't join until "Again".
      if (state.phase === 'picked') return state;
      if (state.fingers.some((f) => f.id === action.id)) return state;
      const finger: Finger = {
        id: action.id,
        x: action.x,
        y: action.y,
        color: nextColor(state.fingers),
        label: nextLabel(state.fingers),
      };
      return withCountdown(state, [...state.fingers, finger], action.now);
    }
    case 'move': {
      if (!state.fingers.some((f) => f.id === action.id)) return state;
      return {
        ...state,
        fingers: state.fingers.map((f) => (f.id === action.id ? { ...f, x: action.x, y: action.y } : f)),
      };
    }
    case 'up': {
      if (!state.fingers.some((f) => f.id === action.id)) return state;
      const fingers = state.fingers.filter((f) => f.id !== action.id);
      if (state.phase === 'picked') {
        // Keep the result on screen: the winner's dot and every team dot stay
        // until "Again"; other lifted fingers just disappear.
        return action.id === state.winnerId || state.teamOf ? state : { ...state, fingers };
      }
      return withCountdown(state, fingers, action.now);
    }
    case 'tick': {
      if (state.phase !== 'counting' || state.deadline === null) return state;
      if (action.now < state.deadline) return state;
      if (state.fingers.length < minFingersFor(state.mode, state.teamSetup)) return state;
      if (state.mode === 'teams') {
        const teamCount = teamCountFor(state.teamSetup, state.fingers.length);
        const teamOf = splitIntoTeams(state.fingers.map((f) => f.id), teamCount, action.random);
        return { ...state, phase: 'picked', deadline: null, teamOf };
      }
      const winner = pickRandom(state.fingers, action.random);
      return { ...state, phase: 'picked', deadline: null, winnerId: winner.id };
    }
    case 'reset':
      // "Again" keeps the chosen mode and team setup.
      return { ...initialPickerState, mode: state.mode, teamSetup: state.teamSetup };
    case 'mode':
    case 'teamSetup': {
      // Changing the setup starts over with whoever is still touching.
      const next =
        action.type === 'mode' ? { ...state, mode: action.mode } : { ...state, teamSetup: action.setup };
      return withCountdown(next, state.phase === 'picked' ? [] : state.fingers, action.now);
    }
    default:
      return state;
  }
}

/**
 * Starting positions for a live play when `firstTeam` starts: the chosen
 * team is 1 and the rest follow in their existing order, wrapping around.
 */
export function startingPositionsFrom(
  teamNames: string[],
  firstTeam: string
): Record<string, { startingPosition: number }> {
  const start = teamNames.indexOf(firstTeam);
  if (start < 0) throw new Error(`Unknown team ${firstTeam}`);
  const result: Record<string, { startingPosition: number }> = {};
  teamNames.forEach((_, i) => {
    const name = teamNames[(start + i) % teamNames.length];
    result[name] = { startingPosition: i + 1 };
  });
  return result;
}
